/* ============================================================================
   SCHEMA — kiểm tra content pack TRƯỚC khi cho nó chạm vào game.

   Vì content đến từ OTA (có thể là file lạ, file hỏng, file của phiên bản khác),
   đây là biên giới không tin cậy. Mọi thứ đi qua đây đều bị soi. Hàm trả về
   DANH SÁCH lỗi thay vì ném ngoại lệ ở lỗi đầu tiên — để báo cáo một lần đủ ý.

   Không dùng thư viện ngoài: giữ core không phụ thuộc, chạy được cả trong Node.
============================================================================ */

type Any = Record<string, unknown>;

const isObj = (v: unknown): v is Any =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);
const isStr = (v: unknown): v is string => typeof v === "string";

class Check {
  errors: string[] = [];
  path: string;
  constructor(path: string) {
    this.path = path;
  }

  private at(k: string) {
    return this.path ? `${this.path}.${k}` : k;
  }

  fail(k: string, why: string) {
    this.errors.push(`${this.at(k)}: ${why}`);
  }

  obj(src: Any, k: string): Any | null {
    const v = src[k];
    if (!isObj(v)) {
      this.fail(k, "phải là object");
      return null;
    }
    return v;
  }

  arr(src: Any, k: string): unknown[] | null {
    const v = src[k];
    if (!Array.isArray(v)) {
      this.fail(k, "phải là mảng");
      return null;
    }
    return v;
  }

  str(src: Any, k: string): string | null {
    const v = src[k];
    if (!isStr(v) || v.length === 0) {
      this.fail(k, "phải là chuỗi khác rỗng");
      return null;
    }
    return v;
  }

  num(src: Any, k: string, min = -Infinity, max = Infinity): number | null {
    const v = src[k];
    if (!isNum(v)) {
      this.fail(k, "phải là số");
      return null;
    }
    if (v < min || v > max) {
      this.fail(k, `phải nằm trong [${min}, ${max}], nhận ${v}`);
      return null;
    }
    return v;
  }

  enumStr<T extends string>(src: Any, k: string, allowed: readonly T[]): T | null {
    const v = src[k];
    if (!isStr(v) || !(allowed as readonly string[]).includes(v)) {
      this.fail(k, `phải là một trong [${allowed.join(", ")}]`);
      return null;
    }
    return v as T;
  }

  child(k: string): Check {
    const c = new Check(this.at(k));
    // gom lỗi con vào cha khi kết thúc — gọi merge() sau khi dùng xong
    return c;
  }

  merge(c: Check) {
    this.errors.push(...c.errors);
  }
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Phải khớp với kiểu CropForm trong src/game/types.ts. */
const CROP_FORMS = [
  "head",
  "herb",
  "bulb",
  "melon",
  "leafy",
  "root",
  "vine",
  "stalk",
  "bush",
  "grain",
  "flower",
] as const;

/** Phải khớp với kiểu FruitShape trong src/game/types.ts. */
const FRUIT_SHAPES = [
  "round",
  "long",
  "cone",
  "lobed",
  "ear",
  "pod",
  "cluster",
  "droop",
  "awn",
  "spike",
  "rosette",
  "disc",
  "pompom",
  "ray",
] as const;

/** Phải khớp với kiểu FruitPattern trong src/game/types.ts. */
const FRUIT_PATTERNS = ["plain", "stripe", "ridge", "net", "speckle"] as const;

/** Phải khớp với kiểu LeafShape trong src/game/types.ts. */
const LEAF_SHAPES = ["drop", "blade", "round", "lobed", "tube"] as const;

function colors(c: Check, src: Any, keys: string[], where: string) {
  for (const k of keys) {
    const v = src[k];
    if (!isStr(v) || !HEX.test(v)) c.fail(`${where}.${k}`, "phải là màu hex dạng #rrggbb");
  }
}

/* -------------------------------------------------------------------------- */

export function validateCrops(raw: unknown): string[] {
  const c = new Check("crops.json");
  if (!isObj(raw)) return ["crops.json: phải là object"];
  const list = c.arr(raw, "crops");
  if (!list) return c.errors;
  if (list.length === 0) c.fail("crops", "phải có ít nhất một cây");

  const seen = new Set<string>();
  list.forEach((item, i) => {
    const k = new Check(`crops[${i}]`);
    if (!isObj(item)) {
      c.fail(`crops[${i}]`, "phải là object");
      return;
    }
    const id = k.str(item, "id");
    if (id) {
      if (seen.has(id)) k.fail("id", `trùng id '${id}'`);
      seen.add(id);
    }
    k.str(item, "name");
    k.str(item, "seedName");
    k.num(item, "seedPrice", 0);
    k.num(item, "sellPrice", 0);
    const gd = k.arr(item, "growthDays");
    if (gd) {
      if (gd.length === 0) k.fail("growthDays", "phải có ít nhất một giai đoạn");
      gd.forEach((d, j) => {
        if (!isNum(d) || d < 1 || !Number.isInteger(d))
          k.fail(`growthDays[${j}]`, "phải là số nguyên >= 1");
      });
    }
    const rg = item["regrowDays"];
    if (rg !== null && (!isNum(rg) || rg < 1))
      k.fail("regrowDays", "phải là null hoặc số >= 1");
    // seasons TUỲ CHỌN: pack cũ không có trường này thì cây gieo được quanh
    // năm — giữ nguyên hành vi trước khi có mùa. Có thì phải là mảng chuỗi;
    // tên mùa lạ được bắt ở loader (chỗ đó mới biết danh sách mùa).
    const seasons = item["seasons"];
    if (seasons !== undefined) {
      if (!Array.isArray(seasons)) k.fail("seasons", "phải là mảng tên mùa");
      else
        seasons.forEach((v, j) => {
          if (!isStr(v)) k.fail(`seasons[${j}]`, "phải là chuỗi");
        });
    }
    const ymin = k.num(item, "yieldMin", 1);
    const ymax = k.num(item, "yieldMax", 1);
    if (ymin !== null && ymax !== null && ymax < ymin)
      k.fail("yieldMax", "phải >= yieldMin");
    if (item["energy"] !== undefined) k.num(item, "energy", 0);
    const art = k.obj(item, "art");
    if (art) {
      // form là TUỲ CHỌN: pack cũ không có trường này vẫn hợp lệ và vẫn vẽ như xưa.
      // Nhưng nếu có mà sai tên thì phải chặn — không lẳng lặng rơi về "leafy",
      // vì như thế một lỗi chính tả sẽ biến cả ruộng dưa thành cây lá.
      if (art["form"] !== undefined)
        k.enumStr(art, "form", CROP_FORMS);
      /* Ba trường dáng của Đợt 24 cũng TUỲ CHỌN và cũng chặn tên sai vì đúng
         một lý do như `form`: rơi lặng lẽ về mặc định thì một lỗi gõ biến quả
         dưa hấu sọc thành một quả bóng trơn, mà build vẫn xanh. */
      if (art["fruitShape"] !== undefined) k.enumStr(art, "fruitShape", FRUIT_SHAPES);
      if (art["pattern"] !== undefined) k.enumStr(art, "pattern", FRUIT_PATTERNS);
      if (art["leafShape"] !== undefined) k.enumStr(art, "leafShape", LEAF_SHAPES);
      colors(k, art, ["stem", "leaf", "leafDark", "fruit", "fruitDark"], "art");
      for (const n of ["height", "leaves", "spread", "fruitCount", "fruitSize"])
        if (!isNum(art[n]) || (art[n] as number) < 0) k.fail(`art.${n}`, "phải là số >= 0");
    }
    c.merge(k);
  });
  return c.errors;
}

export function validateBuildings(raw: unknown): string[] {
  const c = new Check("buildings.json");
  if (!isObj(raw)) return ["buildings.json: phải là object"];
  const list = c.arr(raw, "buildings");
  if (!list) return c.errors;

  const seen = new Set<string>();
  list.forEach((item, i) => {
    const k = new Check(`buildings[${i}]`);
    if (!isObj(item)) {
      c.fail(`buildings[${i}]`, "phải là object");
      return;
    }
    const id = k.str(item, "id");
    if (id) {
      if (seen.has(id)) k.fail("id", `trùng id '${id}'`);
      seen.add(id);
    }
    k.str(item, "name");
    k.str(item, "desc");
    k.num(item, "price", 0);
    k.enumStr(item, "kind", ["floor", "object"] as const);
    if (typeof item["solid"] !== "boolean") k.fail("solid", "phải là boolean");
    if (item["autotile"] !== undefined) k.enumStr(item, "autotile", ["fence"] as const);
    if (item["buildable"] !== undefined && typeof item["buildable"] !== "boolean")
      k.fail("buildable", "phải là boolean (false = chỉ bản đồ dựng sẵn, người chơi không xây)");

    const eff = k.obj(item, "effects");
    if (eff) {
      const allowed = ["waterRadius", "autoWet", "allSeason", "speedMul"];
      for (const key of Object.keys(eff))
        if (!allowed.includes(key))
          k.fail(`effects.${key}`, `hiệu ứng không được core hỗ trợ (core biết: ${allowed.join(", ")})`);
      for (const n of ["waterRadius"])
        if (eff[n] !== undefined && (!isNum(eff[n]) || (eff[n] as number) < 0))
          k.fail(`effects.${n}`, "phải là số >= 0");
      if (eff["autoWet"] !== undefined && typeof eff["autoWet"] !== "boolean")
        k.fail("effects.autoWet", "phải là boolean");
    }

    const art = k.obj(item, "art");
    if (art) colors(k, art, ["body", "dark", "accent"], "art");
    c.merge(k);
  });
  return c.errors;
}

function numsIn(src: Any, keys: string[], path: string): Check {
  const c = new Check(path);
  for (const k of keys)
    if (!isNum(src[k]) || (src[k] as number) < 0) c.fail(k, "phải là số >= 0");
  return c;
}

export function validateItems(raw: unknown): string[] {
  const c = new Check("items.json");
  if (!isObj(raw)) return ["items.json: phải là object"];
  const list = c.arr(raw, "tools");
  if (list)
    list.forEach((item, i) => {
      const k = new Check(`tools[${i}]`);
      if (!isObj(item)) {
        c.fail(`tools[${i}]`, "phải là object");
        return;
      }
      k.str(item, "id");
      k.str(item, "name");
      k.enumStr(item, "action", ["TILL", "WATER", "CHOP", "MINE"] as const);
      if (item["power"] !== undefined) k.num(item, "power", 1, 10);
      if (item["capacity"] !== undefined) k.num(item, "capacity", 1, 999);
      if (item["action"] === "WATER" && item["capacity"] === undefined)
        k.fail("capacity", "công cụ tưới phải khai sức chứa");
      c.merge(k);
    });

  const mats = c.arr(raw, "materials");
  if (mats)
    mats.forEach((m, i) => {
      const k = new Check(`materials[${i}]`);
      if (!isObj(m)) {
        c.fail(`materials[${i}]`, "phải là object");
        return;
      }
      k.str(m, "id");
      k.str(m, "name");
      k.num(m, "sellPrice", 0);
      if (m["buyPrice"] !== undefined) k.num(m, "buyPrice", 0);
      if (m["energy"] !== undefined) k.num(m, "energy", 0);
      if (m["sell"] !== undefined && typeof m["sell"] !== "boolean")
        k.fail("sell", "phải là true/false");
      c.merge(k);
    });
  return c.errors;
}

const INTERACTS = ["SLEEP", "SHOP", "SELL", "REFILL", "CRAFT", "PORTAL", "STORE"] as const;
const TOOL_ACTIONS = ["TILL", "WATER", "CHOP", "MINE"] as const;

export function validateProps(raw: unknown): string[] {
  const c = new Check("props.json");
  if (!isObj(raw)) return ["props.json: phải là object"];
  const list = c.arr(raw, "props");
  if (!list) return c.errors;

  const seen = new Set<string>();
  list.forEach((item, i) => {
    const k = new Check(`props[${i}]`);
    if (!isObj(item)) {
      c.fail(`props[${i}]`, "phải là object");
      return;
    }
    const id = k.str(item, "id");
    if (id) {
      if (seen.has(id)) k.fail("id", `trùng id '${id}'`);
      seen.add(id);
    }
    k.str(item, "name");
    if (typeof item["solid"] !== "boolean") k.fail("solid", "phải là boolean");
    if (item["tall"] !== undefined && typeof item["tall"] !== "boolean")
      k.fail("tall", "phải là boolean");
    if (item["sway"] !== undefined) k.num(item, "sway", 0, 2);
    /* KIỂU HÌNH (Đợt 25): cửa đóng → cửa mở. Phải là số nguyên >= 2 mới có
       nghĩa, và `anim` phải là tên bộ vẽ biết — sai tên thì cánh cửa đứng im
       trong khi build vẫn xanh, đúng kiểu hỏng khó tìm nhất. */
    if (item["frames"] !== undefined) {
      const n = item["frames"];
      if (!isNum(n) || !Number.isInteger(n) || (n as number) < 2)
        k.fail("frames", "phải là số nguyên >= 2");
    }
    if (item["anim"] !== undefined) k.enumStr(item, "anim", ["door"]);
    if (item["seasonal"] !== undefined && typeof item["seasonal"] !== "boolean")
      k.fail("seasonal", "phải là boolean");
    if (item["place"] !== undefined && item["place"] !== "tile" && item["place"] !== "edge")
      k.fail("place", 'phải là "tile" (chiếm trọn ô) hoặc "edge" (đứng ở mép ô)');
    if (item["place"] === "edge" && item["solid"] === true)
      k.fail("place", '"edge" là không chiếm ô nào — không thể vừa không chiếm ô vừa chặn ô');
    if (item["hits"] !== undefined) k.num(item, "hits", 1, 99);
    if (item["tool"] !== undefined) k.enumStr(item, "tool", TOOL_ACTIONS);
    if (item["becomes"] !== undefined && !isStr(item["becomes"]))
      k.fail("becomes", "phải là chuỗi id prop");
    if (item["interact"] !== undefined) k.enumStr(item, "interact", INTERACTS);
    if (item["portable"] !== undefined && typeof item["portable"] !== "boolean")
      k.fail("portable", "phải là boolean");
    /* `move` là danh sách ĐÓNG. Khai sai một chữ thì `cachDoi` trả null và vật
       ấy lặng lẽ thành không dời được — không ai báo, chỉ người chơi bấm hoài
       không lên. */
    if (item["move"] !== undefined && item["move"] !== "lift" && item["move"] !== "drag")
      k.fail("move", 'phải là "lift" (nâng) hoặc "drag" (kéo)');
    /* Ràng buộc CỨNG: vật có `interact` mà dời được thì tay không sẽ NHẤC nó
       thay vì DÙNG nó — `canUseAt` hỏi "dời được không" trước. Cái giường dời
       được là mất luôn cách đi ngủ. */
    if (item["move"] !== undefined && isStr(item["interact"]))
      k.fail("move", `vật có interact (${item["interact"]}) không được dời — sẽ nhấc thay vì dùng`);

    const drops = item["drops"];
    if (drops !== undefined) {
      if (!Array.isArray(drops)) k.fail("drops", "phải là mảng");
      else
        drops.forEach((d, j) => {
          if (!isObj(d)) return k.fail(`drops[${j}]`, "phải là object");
          if (!isStr(d["id"])) k.fail(`drops[${j}].id`, "phải là chuỗi");
          const mn = d["min"];
          const mx = d["max"];
          if (!isNum(mn) || mn < 0) k.fail(`drops[${j}].min`, "phải là số >= 0");
          if (!isNum(mx) || (isNum(mn) && mx < mn)) k.fail(`drops[${j}].max`, "phải >= min");
        });
      if (item["hits"] === undefined) k.fail("drops", "có drops thì phải có hits");
    }

    const portal = item["portal"];
    if (portal !== undefined) {
      if (!isObj(portal)) k.fail("portal", "phải là object {x,y}");
      else {
        if (!isNum(portal["x"]) || !isNum(portal["y"])) k.fail("portal", "x,y phải là số");
      }
      if (item["interact"] !== "PORTAL") k.fail("portal", "chỉ có nghĩa khi interact = PORTAL");
    }
    if (item["interact"] === "PORTAL" && portal === undefined)
      k.fail("interact", "PORTAL thì phải khai portal {x,y}");

    // ---- lớn theo ngày / lan / bị bão quật (core 1.3) ----
    const grow = item["grow"];
    if (grow !== undefined) {
      if (!isObj(grow)) k.fail("grow", "phải là object {to, days}");
      else {
        if (!isStr(grow["to"])) k.fail("grow.to", "phải là chuỗi id prop");
        if (!isNum(grow["days"]) || grow["days"] < 1) k.fail("grow.days", "phải là số >= 1");
      }
    }
    const spread = item["spread"];
    if (spread !== undefined) {
      if (!isObj(spread)) k.fail("spread", "phải là object {chance, into}");
      else {
        if (!isStr(spread["into"])) k.fail("spread.into", "phải là chuỗi id prop");
        if (!isNum(spread["chance"]) || spread["chance"] < 0 || spread["chance"] > 1)
          k.fail("spread.chance", "phải nằm trong [0, 1]");
      }
    }
    const fell = item["stormFell"];
    if (fell !== undefined) {
      if (!isObj(fell)) k.fail("stormFell", "phải là object {to, chance}");
      else {
        if (!isStr(fell["to"])) k.fail("stormFell.to", "phải là chuỗi id prop");
        if (!isNum(fell["chance"]) || fell["chance"] < 0 || fell["chance"] > 1)
          k.fail("stormFell.chance", "phải nằm trong [0, 1]");
      }
    }

    const art = k.obj(item, "art");
    if (art) colors(k, art, ["body", "dark", "accent"], "art");
    c.merge(k);
  });
  return c.errors;
}

/**
 * seasons.json — bốn mùa. TUỲ CHỌN ở cấp pack (pack cũ đã cache không có file
 * này vẫn nạp được, loader sẽ coi như "không có mùa"), nhưng có thì phải đủ.
 */
const ANIMAL_FORMS = ["quadruped", "bird", "fish", "critter"] as const;
const HOUSINGS = ["pen", "free", "water"] as const;
const JOBS = ["patrol", "pest"] as const;

/**
 * actors.json — vật nuôi và sâu bọ. TUỲ CHỌN ở cấp pack (pack cũ đã cache không
 * có file này vẫn nạp được, chỉ là chưa có con vật nào).
 */
export function validateActors(raw: unknown): string[] {
  const c = new Check("actors.json");
  if (!isObj(raw)) return ["actors.json: phải là object"];
  const seen = new Set<string>();

  const one = (item: unknown, where: string) => {
    const k = new Check(where);
    if (!isObj(item)) {
      c.fail(where, "phải là object");
      return;
    }
    const id = k.str(item, "id");
    if (id) {
      if (seen.has(id)) k.fail("id", `trùng id '${id}'`);
      seen.add(id);
    }
    k.str(item, "name");
    k.num(item, "price", 0);
    k.enumStr(item, "housing", HOUSINGS);
    if (item["pen"] !== undefined && !isStr(item["pen"]))
      k.fail("pen", "phải là id một khu trong tiles.json:pens");
    /* Nhận cả ba dạng: mảng (chuẩn), một chuỗi hoặc null (pack cũ trước khi
       mỗi loài có nhiều món ăn). Loader chuẩn hoá về mảng. */
    const feed = item["feed"];
    if (feed !== undefined && feed !== null && !isStr(feed)) {
      if (!Array.isArray(feed)) k.fail("feed", "phải là mảng id vật phẩm (hoặc một chuỗi / null cho pack cũ)");
      else feed.forEach((v, j) => { if (!isStr(v)) k.fail(`feed[${j}]`, "phải là id vật phẩm"); });
    }
    if (item["pecks"] !== undefined && typeof item["pecks"] !== "boolean")
      k.fail("pecks", "phải là boolean (true = mổ sâu trên nền cỏ thường)");
    k.num(item, "fedMinutes", 1, 100000);
    k.num(item, "matureDays", 0, 999);
    k.num(item, "starveDays", 0, 99999);
    k.num(item, "speed", 1, 400);
    if (item["job"] !== undefined) k.enumStr(item, "job", JOBS);

    const box = k.obj(item, "box");
    if (box) {
      k.num(box as Record<string, unknown>, "w", 1, 64);
      k.num(box as Record<string, unknown>, "h", 1, 64);
    }

    const prods = k.arr(item, "products");
    if (prods)
      prods.forEach((p, j) => {
        if (!isObj(p)) {
          k.fail(`products[${j}]`, "phải là object");
          return;
        }
        if (!isStr(p["id"])) k.fail(`products[${j}].id`, "phải là chuỗi");
        k.num(p, "every", 1, 999);
        k.num(p, "min", 0);
        k.num(p, "max", 0);
        // `max < min` hỏng LẶNG: `randInt` gặp `max <= min` thì trả về `min`,
        // nên một pack ghi ngược hai số vẫn chạy, chỉ là sản lượng khoá cứng ở
        // cận dưới mãi mãi. Cây trồng đã kiểm điều này, vật nuôi thì chưa.
        if (typeof p["min"] === "number" && typeof p["max"] === "number" && p["max"] < p["min"])
          k.fail(`products[${j}].max`, "phải >= min");
      });

    const meat = item["meat"];
    if (meat !== null && meat !== undefined) {
      if (!isObj(meat)) k.fail("meat", "phải là object hoặc null");
      else {
        if (!isStr(meat["id"])) k.fail("meat.id", "phải là chuỗi");
        k.num(meat, "min", 0);
        k.num(meat, "max", 0);
        if (
          typeof meat["min"] === "number" &&
          typeof meat["max"] === "number" &&
          meat["max"] < meat["min"]
        )
          k.fail("meat.max", "phải >= min");
      }
    }

    const art = k.obj(item, "art");
    if (art) {
      k.enumStr(art as Record<string, unknown>, "form", ANIMAL_FORMS);
      for (const key of ["body", "bodyDark", "belly", "accent"])
        if (!isStr(art[key]) || !/^#[0-9a-fA-F]{6}$/.test(art[key] as string))
          k.fail(`art.${key}`, "phải là mã màu #rrggbb");
      k.num(art as Record<string, unknown>, "w", 1, 16);
      k.num(art as Record<string, unknown>, "h", 1, 16);
      for (const key of ["fluff", "patch", "snout", "crest"])
        if (art[key] !== undefined) k.num(art as Record<string, unknown>, key, 0, 1);
      if (art["horn"] !== undefined) k.num(art as Record<string, unknown>, "horn", 0, 3);
      if (art["tailUp"] !== undefined && typeof art["tailUp"] !== "boolean")
        k.fail("art.tailUp", "phải là boolean");
    }
    c.merge(k);
  };

  for (const group of ["animals", "pests"] as const) {
    const list = c.arr(raw, group);
    if (!list) continue;
    list.forEach((item, i) => one(item, `${group}[${i}]`));
  }

  const vs = raw["vehicles"];
  if (vs !== undefined) {
    const list = c.arr(raw, "vehicles");
    if (list)
      list.forEach((item, i) => {
        const k = new Check(`vehicles[${i}]`);
        if (!isObj(item)) {
          c.fail(`vehicles[${i}]`, "phải là object");
          return;
        }
        k.str(item, "id");
        k.str(item, "name");
        k.num(item, "price", 0);
        k.num(item, "capacity", 1, 100000);
        k.num(item, "speed", 1, 400);
        if (item["buyBonus"] !== undefined) k.num(item, "buyBonus", 0, 5);
        /* DÁNG XE: danh sách ĐÓNG. Khai sai một chữ thì lớp vẽ lặng lẽ rơi về
           dáng mặc định và cái xe buýt hiện ra là một chiếc xe tải — hỏng theo
           kiểu không ai báo. Chặn ngay ở cổng content. */
        if (item["style"] !== undefined) {
          const DANG = ["box", "flatbed", "boat", "bus", "moto"];
          if (!isStr(item["style"]) || !DANG.includes(item["style"] as string))
            k.fail("style", `phải là một trong ${DANG.join(" · ")}`);
        }
        const box = k.obj(item, "box");
        if (box) {
          k.num(box as Record<string, unknown>, "w", 1, 64);
          k.num(box as Record<string, unknown>, "h", 1, 64);
        }
        const art = k.obj(item, "art");
        if (art)
          for (const key of ["body", "dark", "glass", "accent"])
            if (!isStr(art[key]) || !/^#[0-9a-fA-F]{6}$/.test(art[key] as string))
              k.fail(`art.${key}`, "phải là mã màu #rrggbb");
        c.merge(k);
      });
  }

  const w = raw["workers"];
  if (w !== undefined) {
    const k = new Check("workers");
    if (!isObj(w)) c.fail("workers", "phải là object");
    else {
      for (const [key, lo, hi] of [
        ["hireFee", 0, 1e9],
        ["wage", 0, 1e9],
        ["wageEveryDays", 1, 365],
        ["carryMax", 1, 9999],
        ["energyMax", 1, 9999],
        ["energyPerTask", 0, 9999],
        ["restBelow", 0, 9999],
        ["restMinutes", 0, 100000],
        ["speed", 1, 400],
      ] as [string, number, number][])
        k.num(w, key, lo, hi);
      const box = k.obj(w, "box");
      if (box) {
        k.num(box as Record<string, unknown>, "w", 1, 64);
        k.num(box as Record<string, unknown>, "h", 1, 64);
      }
      const names = w["names"];
      if (names !== undefined) {
        if (!Array.isArray(names) || !names.length || !names.every(isStr))
          k.fail("names", "phải là mảng tên (chuỗi), ít nhất một tên");
      }
      const skins = k.arr(w, "skins");
      if (skins) {
        if (!skins.length) k.fail("skins", "phải có ít nhất một bộ đồ");
        skins.forEach((sk, i) => {
          if (!isObj(sk)) {
            k.fail(`skins[${i}]`, "phải là object");
            return;
          }
          for (const key of ["shirt", "shirtDark", "pants", "cap", "hair"])
            if (!isStr(sk[key]) || !/^#[0-9a-fA-F]{6}$/.test(sk[key] as string))
              k.fail(`skins[${i}].${key}`, "phải là mã màu #rrggbb");
        });
      }
      c.merge(k);
    }
  }
  return c.errors;
}

export function validateSeasons(raw: unknown): string[] {
  const c = new Check("seasons.json");
  if (!isObj(raw)) return ["seasons.json: phải là object"];
  c.num(raw, "daysPerSeason", 1, 365);
  const list = c.arr(raw, "seasons");
  if (!list) return c.errors;
  if (list.length === 0) c.fail("seasons", "phải có ít nhất một mùa");
  const seen = new Set<string>();
  list.forEach((item, i) => {
    const k = new Check(`seasons[${i}]`);
    if (!isObj(item)) {
      c.fail(`seasons[${i}]`, "phải là object");
      return;
    }
    const id = k.str(item, "id");
    if (id) {
      if (seen.has(id)) k.fail("id", `trùng id '${id}'`);
      seen.add(id);
    }
    k.str(item, "name");
    k.num(item, "growMul", 0, 10);
    const w = k.obj(item, "weather");
    if (w)
      for (const [wid, v] of Object.entries(w))
        if (!isNum(v) || v < 0) k.fail(`weather.${wid}`, "phải là số >= 0");
    if (item["tint"] !== undefined) {
      const t = k.obj(item, "tint");
      if (t) {
        if (!isStr(t["color"]) || !/^#[0-9a-fA-F]{6}$/.test(t["color"] as string))
          k.fail("tint.color", "phải là mã màu dạng #rrggbb");
        k.num(t as Record<string, unknown>, "alpha", 0, 1);
        if (t["desat"] !== undefined) k.num(t as Record<string, unknown>, "desat", 0, 1);
      }
    }
    c.merge(k);
  });
  return c.errors;
}

export function validateWeather(raw: unknown): string[] {
  const c = new Check("weather.json");
  if (!isObj(raw)) return ["weather.json: phải là object"];
  const list = c.arr(raw, "weathers");
  if (!list) return c.errors;
  if (list.length === 0) c.fail("weathers", "phải có ít nhất một kiểu thời tiết");
  const seen = new Set<string>();
  let totalWeight = 0;
  list.forEach((item, i) => {
    const k = new Check(`weathers[${i}]`);
    if (!isObj(item)) {
      c.fail(`weathers[${i}]`, "phải là object");
      return;
    }
    const id = k.str(item, "id");
    if (id) {
      if (seen.has(id)) k.fail("id", `trùng id '${id}'`);
      seen.add(id);
    }
    k.str(item, "name");
    const w = k.num(item, "weight", 0);
    if (w !== null) totalWeight += w;
    if (typeof item["wet"] !== "boolean") k.fail("wet", "phải là boolean");
    k.num(item, "growMul", 0, 10);
    k.num(item, "wind", 0, 1);
    if (item["hot"] !== undefined && typeof item["hot"] !== "boolean") k.fail("hot", "phải là boolean");
    // Hệ số tốc độ chỉ được LÀM CHẬM: > 1 thì trời mưa chạy nhanh hơn trời nắng,
    // và < 0.3 thì cả nông trại đứng hình mà không ai hiểu vì sao.
    if (item["speedMul"] !== undefined) k.num(item, "speedMul", 0.3, 1);
    if (item["shelter"] !== undefined && typeof item["shelter"] !== "boolean") k.fail("shelter", "phải là boolean");
    if (item["halt"] !== undefined && typeof item["halt"] !== "boolean") k.fail("halt", "phải là boolean");
    if (item["diseaseMul"] !== undefined) k.num(item, "diseaseMul", 0, 20);
    if (item["fogUntil"] !== undefined) k.num(item, "fogUntil", 0, 2880);
    const streak = item["streak"];
    if (streak !== undefined) {
      if (!isObj(streak)) k.fail("streak", "phải là object {max, chance}");
      else {
        if (!isNum(streak["max"]) || streak["max"] < 1) k.fail("streak.max", "phải là số >= 1");
        if (!isNum(streak["chance"]) || streak["chance"] < 0 || streak["chance"] > 1)
          k.fail("streak.chance", "phải nằm trong [0, 1]");
      }
    }
    const storm = item["storm"];
    if (storm !== undefined) {
      if (!isObj(storm)) k.fail("storm", "phải là object {cropChance}");
      else if (!isNum(storm["cropChance"]) || storm["cropChance"] < 0 || storm["cropChance"] > 1)
        k.fail("storm.cropChance", "phải nằm trong [0, 1]");
    }
    c.merge(k);
  });
  if (list.length > 0 && !(totalWeight > 0)) c.fail("weathers", "tổng weight phải > 0, không thì không rút thăm được");
  const first = c.str(raw, "firstDay");
  if (first && !seen.has(first)) c.fail("firstDay", `'${first}' không có trong weathers`);
  return c.errors;
}

/** Tên công thức đã tự chép số lượng vào ("Đường nhựa ×4") — bảng chế tạo in
 *  '×n' từ `out.n`, nên tên như thế hiện ra thành "Đường nhựa ×4 ×4". */
const TEN_CO_SO_LUONG = /[×x]\s*\d+\s*$/;

export function validateRecipes(raw: unknown): string[] {
  const c = new Check("recipes.json");
  if (!isObj(raw)) return ["recipes.json: phải là object"];
  const list = c.arr(raw, "recipes");
  if (!list) return c.errors;
  const seen = new Set<string>();
  list.forEach((item, i) => {
    const k = new Check(`recipes[${i}]`);
    if (!isObj(item)) {
      c.fail(`recipes[${i}]`, "phải là object");
      return;
    }
    const id = k.str(item, "id");
    if (id) {
      if (seen.has(id)) k.fail("id", `trùng id '${id}'`);
      seen.add(id);
    }
    const ten = k.str(item, "name");
    const out = k.obj(item, "out");
    if (out) {
      if (!isStr(out["id"])) k.fail("out.id", "phải là chuỗi");
      if (!isNum(out["n"]) || (out["n"] as number) < 1) k.fail("out.n", "phải là số >= 1");
      // SỐ LƯỢNG có đúng MỘT nguồn là `out.n`. Chép nó vào tên nữa thì bảng chế
      // tạo in ra "Đường nhựa ×4 ×4" — sai không gây crash nên sống rất dai.
      if (ten && (out["n"] as number) > 1 && TEN_CO_SO_LUONG.test(ten))
        k.fail(
          "name",
          `'${ten}' đã tự chép số lượng vào tên — bảng chế tạo in '×${out["n"]}' từ out.n rồi`,
        );
    }
    const ins = k.arr(item, "in");
    if (ins) {
      if (!ins.length) k.fail("in", "phải có ít nhất một nguyên liệu");
      ins.forEach((v, j) => {
        if (!isObj(v)) return k.fail(`in[${j}]`, "phải là object");
        if (!isStr(v["id"])) k.fail(`in[${j}].id`, "phải là chuỗi");
        if (!isNum(v["n"]) || (v["n"] as number) < 1) k.fail(`in[${j}].n`, "phải là số >= 1");
      });
    }
    c.merge(k);
  });
  return c.errors;
}

export function validateBalance(raw: unknown): string[] {
  const c = new Check("balance.json");
  if (!isObj(raw)) return ["balance.json: phải là object"];
  c.num(raw, "startMoney", 0);
  c.num(raw, "energyMax", 1);
  c.num(raw, "dayStartMinutes", 0, 1440);
  const end = c.num(raw, "dayEndMinutes", 0, 2880);
  const start = raw["dayStartMinutes"];
  if (isNum(start) && end !== null && end <= start)
    c.fail("dayEndMinutes", "phải lớn hơn dayStartMinutes");
  c.num(raw, "realSecondsPerGameTenMinutes", 0.01);
  c.num(raw, "sleepRestore", 0, 1);
  c.num(raw, "lateSleepPenalty", 0, 1);
  c.num(raw, "passOutEnergy", 0, 1);
  c.num(raw, "inventorySlots", 1, 200);
  const hb = c.num(raw, "hotbarSlots", 1, 20);
  const inv = raw["inventorySlots"];
  if (hb !== null && isNum(inv) && hb > inv)
    c.fail("hotbarSlots", "không được lớn hơn inventorySlots");

  const cost = c.obj(raw, "energyCost");
  if (cost) {
    c.merge(
      numsIn(cost, ["till", "water", "plant", "harvest", "build", "chop", "mine"], "energyCost"),
    );
    for (const k of ["cure", "pull"])
      if (cost[k] !== undefined && (!isNum(cost[k]) || (cost[k] as number) < 0))
        c.fail(`energyCost.${k}`, "phải là số >= 0");
  }
  // core 1.3 — tuỳ chọn, có thì phải hợp lệ
  for (const [k, min, max] of [
    ["diseaseChance", 0, 1],
    // Trần rộng tay: xác suất cuối đã bị kẹp về 1, nên số lớn chỉ có nghĩa
    // "kề cây bệnh là chắc chắn lây". Với diseaseChance nhỏ (0,001) thì phải
    // tới ba chữ số mới diễn đạt được ý đó — chặn ở 50 là chặn nhầm thiết kế
    // hợp lệ. Vẫn đủ để bắt lỗi gõ nhầm kiểu 1e9.
    ["diseaseNeighbourMul", 0, 1000],
    ["sickYieldMul", 0, 1],
    ["noonDryMinutes", 0, 2880],
    // 0 = tắt hẳn (luống không bao giờ mọc cỏ lại)
    ["tilledIdleDays", 0, 999],
    ["troughMax", 1, 999],
    ["forestRegrowChance", 0, 1],
    ["sleepSeconds", 0, 10],
  ] as [string, number, number][])
    if (raw[k] !== undefined) c.num(raw, k, min, max);

  // Ba trường dưới đây được thêm ở core 1.1: cho phép THIẾU để pack cũ đã cache
  // vẫn dùng được (loader sẽ điền giá trị mặc định), nhưng có thì phải hợp lệ.
  for (const [k, min, max] of [
    ["moveSpeed", 8, 400],
    ["runSpeed", 8, 600],
    ["actionSeconds", 0, 5],
    ["actionImpact", 0, 1],
  ] as [string, number, number][])
    if (raw[k] !== undefined) c.num(raw, k, min, max);
  if (isNum(raw["moveSpeed"]) && isNum(raw["runSpeed"]) && raw["runSpeed"] < raw["moveSpeed"])
    c.fail("runSpeed", "không được nhỏ hơn moveSpeed");

  const seeds = c.obj(raw, "startSeeds");
  if (seeds)
    for (const [k, v] of Object.entries(seeds))
      if (!isNum(v) || v < 0) c.fail(`startSeeds.${k}`, "phải là số >= 0");
  return c.errors;
}

/* Danh sách NỀN mà core biết vẽ và biết luật. Thêm một nền là việc LÀN CHẬM:
   phải có mã vẽ trong `atlas.ts` + `draw.ts`, nên không để content tự bịa ra
   một tên nền mới rồi ra ô vô hình. */
const GROUNDS = ["grass", "path", "water", "wood", "asphalt", "concrete"] as const;
const ZONE_KINDS = ["farm", "forest"] as const;

export function validateTiles(raw: unknown): string[] {
  const c = new Check("tiles.json");
  if (!isObj(raw)) return ["tiles.json: phải là object"];

  const grounds = c.obj(raw, "grounds");
  if (grounds)
    for (const [name, v] of Object.entries(grounds)) {
      if (!(GROUNDS as readonly string[]).includes(name))
        c.fail(`grounds.${name}`, `nền không có thật (core biết: ${GROUNDS.join(", ")})`);
      if (!isObj(v)) {
        c.fail(`grounds.${name}`, "phải là object");
        continue;
      }
      const k = new Check(`grounds.${name}`);
      if (v["solid"] !== undefined && typeof v["solid"] !== "boolean")
        k.fail("solid", "phải là boolean");
      if (v["interact"] !== undefined) k.enumStr(v, "interact", INTERACTS);
      // Trần 4: nhanh hơn thế thì nhân vật lướt qua ô nhanh hơn một khung hình
      // và va chạm bắt đầu bỏ sót tường. Sàn 0,1 để không có nền nào đứng hình.
      if (v["speedMul"] !== undefined) k.num(v as Record<string, unknown>, "speedMul", 0.1, 4);
      c.merge(k);
    }

  const legend = c.obj(raw, "legend");
  if (legend) {
    for (const [ch, v] of Object.entries(legend)) {
      if (ch.length !== 1) c.fail(`legend.${ch}`, "khoá phải đúng MỘT ký tự");
      if (!isObj(v)) {
        c.fail(`legend.${ch}`, "phải là object");
        continue;
      }
      const k = new Check(`legend.${ch}`);
      k.enumStr(v, "ground", GROUNDS);
      if (v["build"] !== undefined && !isStr(v["build"]))
        k.fail("build", "phải là id một công trình trong buildings.json");
      c.merge(k);
    }
  }
  const pens = raw["pens"];
  if (pens !== undefined) {
    if (!Array.isArray(pens)) c.fail("pens", "phải là mảng các khu chuồng");
    else {
      const seenPen = new Set<string>();
      pens.forEach((v, i) => {
        const k = new Check(`pens[${i}]`);
        if (!isObj(v)) {
          c.fail(`pens[${i}]`, "phải là object");
          return;
        }
        const id = k.str(v, "id");
        if (id) {
          if (seenPen.has(id)) k.fail("id", `trùng id '${id}'`);
          seenPen.add(id);
        }
        k.str(v, "name");
        k.str(v, "map");
        k.num(v, "x", 0, 1000);
        k.num(v, "y", 0, 1000);
        // Ruột phải có thật: khu rộng 0 ô là khu mà không con nào về được.
        k.num(v, "w", 1, 1000);
        k.num(v, "h", 1, 1000);
        const pf = v["feeds"];
        if (pf !== undefined) {
          if (!Array.isArray(pf)) k.fail("feeds", "phải là mảng id vật phẩm đổ được vào máng khu này");
          else pf.forEach((q, j) => { if (!isStr(q)) k.fail(`feeds[${j}]`, "phải là id vật phẩm"); });
        }
        if (v["swim"] !== undefined && typeof v["swim"] !== "boolean")
          k.fail("swim", "phải là boolean (true = khu dưới nước, ruột là ô nước)");
        c.merge(k);
      });
    }
  }

  const zones = raw["zones"];
  if (zones !== undefined) {
    if (!Array.isArray(zones)) c.fail("zones", "phải là mảng các vùng đất");
    else {
      const seenZone = new Set<string>();
      zones.forEach((v, i) => {
        const k = new Check(`zones[${i}]`);
        if (!isObj(v)) {
          c.fail(`zones[${i}]`, "phải là object");
          return;
        }
        const id = k.str(v, "id");
        if (id) {
          if (seenZone.has(id)) k.fail("id", `trùng id '${id}'`);
          seenZone.add(id);
        }
        k.str(v, "name");
        k.enumStr(v, "kind", ZONE_KINDS);
        k.str(v, "map");
        k.num(v, "x", 0, 1000);
        k.num(v, "y", 0, 1000);
        k.num(v, "w", 1, 1000);
        k.num(v, "h", 1, 1000);
        c.merge(k);
      });
    }
  }

  const signs = raw["signs"];
  if (signs !== undefined) {
    if (!Array.isArray(signs)) c.fail("signs", "phải là mảng các tấm biển");
    else
      signs.forEach((v, i) => {
        const k = new Check(`signs[${i}]`);
        if (!isObj(v)) {
          c.fail(`signs[${i}]`, "phải là object");
          return;
        }
        k.str(v, "map");
        k.num(v, "x", 0, 1000);
        k.num(v, "y", 0, 1000);
        /* Chữ trên biển nằm ĐÈ lên mặt ruộng. Dài quá thì nó che mất chính cái
           lô mà nó đang gọi tên, nên chặn ngay ở đây thay vì để người thiết kế
           tự phát hiện lúc nhìn màn hình. */
        const t = k.str(v, "text");
        if (t !== null && t.length > 18)
          k.fail("text", `dài ${t.length} ký tự — biển cắm chỉ chứa được 18`);
        if (v["side"] !== undefined) k.enumStr(v, "side", ["e", "w"] as const);
        if (v["style"] !== undefined) k.enumStr(v, "style", ["post", "facade"] as const);
        /* BIỂN HIỆU MẶT TIỀN phải biết công trình rộng mấy ô — chữ căn giữa
           trên ngần ấy ô. Thiếu `w` thì nó căn giữa trên đúng một ô và lệch
           hẳn sang mép trái toà nhà. */
        if (v["style"] === "facade") {
          const w = k.num(v, "w", 1, 40);
          if (w === null) k.fail("w", "biển mặt tiền phải khai bề ngang công trình");
        } else if (v["w"] !== undefined) {
          k.fail("w", "chỉ biển mặt tiền mới dùng `w`");
        }
        c.merge(k);
      });
  }

  const spawn = c.obj(raw, "spawn");
  if (spawn) c.merge(numsIn(spawn, ["x", "y"], "spawn"));
  const indoor = raw["indoorMaps"];
  if (indoor !== undefined) {
    if (!Array.isArray(indoor)) c.fail("indoorMaps", "phải là mảng tên bản đồ");
    else indoor.forEach((v, i) => { if (!isStr(v)) c.fail(`indoorMaps[${i}]`, "phải là chuỗi"); });
  }
  return c.errors;
}

export function validateMap(raw: unknown, legendChars: Set<string>): string[] {
  const c = new Check("maps/farm.json");
  if (!isObj(raw)) return ["maps/farm.json: phải là object"];
  const w = c.num(raw, "w", 1, 1000);
  const h = c.num(raw, "h", 1, 1000);
  const rows = c.arr(raw, "rows");
  if (!rows || w === null || h === null) return c.errors;
  if (rows.length !== h) c.fail("rows", `phải có đúng ${h} hàng, nhận ${rows.length}`);

  const unknown = new Set<string>();
  rows.forEach((r, y) => {
    if (!isStr(r)) {
      c.fail(`rows[${y}]`, "phải là chuỗi");
      return;
    }
    if (r.length !== w) c.fail(`rows[${y}]`, `phải dài ${w} ký tự, nhận ${r.length}`);
    for (const ch of r) if (!legendChars.has(ch)) unknown.add(ch);
  });
  if (unknown.size)
    c.fail("rows", `dùng ký tự không có trong tiles.json legend: ${[...unknown].join(" ")}`);
  return c.errors;
}

export function validateProgression(raw: unknown): string[] {
  const c = new Check("progression.json");
  if (!isObj(raw)) return ["progression.json: phải là object"];
  for (const key of ["stages", "goals"]) {
    const list = c.arr(raw, key);
    if (!list) continue;
    const seen = new Set<string>();
    list.forEach((item, i) => {
      const k = new Check(`${key}[${i}]`);
      if (!isObj(item)) {
        c.fail(`${key}[${i}]`, "phải là object");
        return;
      }
      const id = k.str(item, "id");
      if (id) {
        if (seen.has(id)) k.fail("id", `trùng id '${id}'`);
        seen.add(id);
      }
      if (key === "stages") {
        k.str(item, "name");
        /* `unlocks` đã bỏ hẳn: cửa hàng bán mọi thứ ngay từ đầu. Pack cũ còn
           trường đó thì BỎ QUA, không báo lỗi — nó chỉ là dữ liệu thừa, không
           làm hỏng gì, và từ chối cả pack vì một trường thừa là quá tay. */
        const rw = item["reward"];
        if (rw !== undefined) {
          if (!isObj(rw)) k.fail("reward", "phải là object { money?, items? }");
          else {
            if (rw["money"] !== undefined) k.num(rw, "money", 0);
            const its = rw["items"];
            if (its !== undefined) {
              if (!Array.isArray(its)) k.fail("reward.items", "phải là mảng { id, n }");
              else
                its.forEach((v, j) => {
                  if (!isObj(v)) return k.fail(`reward.items[${j}]`, "phải là object");
                  if (!isStr(v["id"])) k.fail(`reward.items[${j}].id`, "phải là id vật phẩm");
                  if (!isNum(v["n"]) || (v["n"] as number) < 1) k.fail(`reward.items[${j}].n`, "phải là số >= 1");
                });
            }
          }
        }
      } else {
        k.str(item, "text");
      }
      const req = k.obj(item, "require");
      if (req)
        for (const [rk, rv] of Object.entries(req))
          if (!isNum(rv)) k.fail(`require.${rk}`, "phải là số");
      c.merge(k);
    });
  }
  return c.errors;
}

export function validateStrings(raw: unknown): string[] {
  const c = new Check("strings.json");
  if (!isObj(raw)) return ["strings.json: phải là object"];
  c.str(raw, "lang");
  c.obj(raw, "ui");
  c.obj(raw, "msg");
  return c.errors;
}

export function validateManifest(raw: unknown): string[] {
  const c = new Check("manifest.json");
  if (!isObj(raw)) return ["manifest.json: phải là object"];
  const v = c.str(raw, "contentVersion");
  if (v && !/^\d+\.\d+\.\d+$/.test(v))
    c.fail("contentVersion", "phải đúng dạng semver x.y.z");
  const rc = c.str(raw, "requiresCore");
  if (rc && !/^[\^~]?\d+\.\d+\.\d+$/.test(rc))
    c.fail("requiresCore", "phải là dải semver dạng ^1.0.0 / ~1.2.0 / 1.0.0");
  return c.errors;
}
